<template>
  <div v-if="isFetching" class="flex min-h-full bg-base rounded-2 mr-3">
    <div class="w-full pa-2">
      <NSkeleton
        v-for="n in numOfSkeletons"
        :key="n"
        class="rounded"
        :class="{ 'mt-1': n > 1 }"
        :height="125"
        width="100%"
      />
    </div>
  </div>
  <FoldersFilesTable
    v-else-if="shouldDisplayFilesAndFolders"
    :class="{ 'pr-3': isContentPanelOpen || selectedSection === 'all' }"
  />
  <FoldersFilesEmpty v-else />
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref, watch, type ComputedRef } from 'vue'
import { NSkeleton } from 'naive-ui'

import FoldersFilesTable from '@browser/components/Content/AllContent/Table/FoldersFilesTable.vue'
import FoldersFilesEmpty from '@browser/components/Content/AllContent/Empty/FoldersFilesEmpty.vue'

import useAppStore from '@browser/stores/app'
import useApiStore from '@browser/stores/api'
import { usePopularity, EntityType } from '@canvas-builder/composables/usePopularity'

import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { isContentPanelOpen, selectedSection, displayedFiles, displayedFilesFolders } = useAppStore()
const { state: apiState } = useApiStore()
const { fetchPopularityData } = usePopularity()

const isFetching = computed(
  () => apiState.isFetchingFolderItems || apiState.isFetchingFavorites || apiState.isFetchingListFiles,
)
const numOfSkeletons = ref(5)
const shouldDisplayFilesAndFolders = computed(() => {
  const shouldRenderListItems = apiState.searchQuery
    ? !!displayedFilesFolders.value.length
    : !!(displayedFiles.value.length || apiState.folders.length)

  return shouldRenderListItems
})
watch(
  () => apiState.isFetchingFolderItems,
  (isfetching) => {
    if (isfetching) {
      const fetchingFolder = apiState.folders?.find((folder) => folder.id === apiState.fetchingFolderId)
      if (fetchingFolder) {
        // Use eligible counts if available, fallback to regular counts
        const fileCount = fetchingFolder.available_files_count ?? fetchingFolder.files_count ?? 0
        const folderCount = fetchingFolder.available_folders_count ?? fetchingFolder.folders_count ?? 0
        const num = fileCount + folderCount
        numOfSkeletons.value = num > 0 ? num : 5
      }
    }
  },
)

onMounted(() => {
  if (launchDarkly.value.show_popularity_icons) {
    fetchPopularityData(EntityType.FILE)
  }
})
</script>
